import React, { useState, useEffect } from "react";
import "./index.scss";
import { TabPane, Row, Col } from "reactstrap";
import { Button } from "shared";
import ModalCreateContract from "components/ModalCreateContract";
import ApiCaller from "api/apiCaller";
import ContractItem from "./ContractItem";
import t from "utils/Lang";
import ContractTable from "./ContractTable";
import { getContractLocation } from "utils/SiteHelper";

type ContractTabProps = {
  isView: boolean;
  siteData: any;
  setSiteData: Function;
  siteId: string;
};

const ContractTab = ({
  isView,
  siteData,
  setSiteData,
  siteId,
}: ContractTabProps) => {
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [isOpenCreateContract, setOpenCreateContract] = useState(false);
  const [contractTypes, setContractTypes] = useState<Array<any>>([]);
  const toggle = () => setOpenCreateContract(!isOpenCreateContract);
  useEffect(() => {
    getContractTypes();
  }, []);
  const getContractTypes = async () => {
    const contractTypeRes = await ApiCaller.get("contract_type/");
    if (contractTypeRes.result) {
      setContractTypes(contractTypeRes.result);
    }
  };
  const uploadAttachment = async (id: number, files: Array<any>) => {
    if (!id || files.length === 0) return;
    const req: Array<any> = [];
    files.forEach((f) => {
      const attachmentFormData = new FormData();
      attachmentFormData.append("user_file", f.file);
      attachmentFormData.append(
        "attachment_content_relation_type",
        f.attribute
      );
      req.push(ApiCaller.post(`attachment/contract/${id}`, attachmentFormData));
      if (f.prevId) {
        req.push(ApiCaller.delete(`attachment/${f.prevId}`));
      }
    });
    await Promise.all(req);
  };
  const onApply = async (data: any, paymentTerms: Array<any>, taxData: any) => {
    data.deposit_fee = data.deposit_fee ? parseInt(data.deposit_fee) : 0;
    data.payment_period_monthly_cycle = data.payment_period_monthly_cycle
      ? parseInt(data.payment_period_monthly_cycle)
      : 0;
    const newContracts = siteData.contracts || [];
    const paymentTermsBody = paymentTerms.map((el, index) => {
      if (index === 0) {
        el.increase_fee_rate = null;
      }
      el.revenue_share_percentage = el.revenue_share_percentage
        ? parseInt(el.revenue_share_percentage) / 100
        : null;
      return el;
    });
    const contract_tax: any = {
      valid_from: data.available_from,
      valid_to: data.available_to,
      tax_percentage: taxData.tax_percentage / 100,
      contract_tax_type_id: taxData.contract_tax_type_id,
    };
    const files = paymentTerms
      .map((el, index) => ({
        attribute: `payment_file_${index}`,
        file: el.payment_file?.file,
        prevId: el.payment_file?.prevId,
      }))
      .filter((el) => !!el.file);
    if (data.contract_file?.file) {
      files.push({
        attribute: "contract_file",
        file: data.contract_file?.file,
        prevId: data.contract_file?.prevId,
      });
    }
    if (data.action === "create") {
      const res = await ApiCaller.post("contract", {
        ...data,
        site_id: siteId,
      });
      await uploadAttachment(res.result.id, files);
      if (res.status == "ok") {
        data.id = res.result.id;
        const taxRes = await ApiCaller.post(
          `contract/${res.result.id}/contract_tax`,
          {
            contract_taxes: [contract_tax],
          }
        );
        await ApiCaller.post(`contract/${res.result.id}/payment_term`, {
          payment_terms: paymentTermsBody,
        });
        contract_tax.id = taxRes?.result?.id;
        data.contract_taxes = [contract_tax];
        data.payment_terms = paymentTermsBody;
        newContracts.push(data);
      }
    } else if (data.action === "edit") {
      await uploadAttachment(data.id, files);
      const res = await ApiCaller.put(`contract/${data.id}`, data);
      const taxRes = await ApiCaller.post(
        `contract/${res.result.id}/contract_tax`,
        {
          contract_taxes: [contract_tax],
        }
      );
      await ApiCaller.post(`contract/${data.id}/payment_term`, {
        payment_terms: paymentTermsBody,
      });
      if (res.status == "ok") {
        contract_tax.id = taxRes?.result?.id;
        const idx = newContracts.findIndex((ct: any) => ct.id === data.id);
        data.payment_terms = paymentTermsBody;
        data.contract_taxes = [contract_tax];
        newContracts[idx] = data;
      }
    } else if (data.action === "delete") {
      await ApiCaller.delete(`contract/${data.id}`);
      const idx = newContracts.findIndex((ct: any) => ct.id === data.id);
      newContracts[idx] = data;
    }
    setSiteData((siteData: any) => ({
      ...siteData,
      contracts: newContracts,
    }));
    toggle();
  };
  const onDone = async (contract: any) => {
    const newContracts = siteData.contracts || [];
    contract.is_can_edit = false;
    const res = await ApiCaller.put(`contract/${contract.id}`, contract);
    if (res.status == "ok") {
      const idx = newContracts.findIndex((ct: any) => ct.id === contract.id);
      newContracts[idx] = contract;
    }
    setSiteData((siteData: any) => ({
      ...siteData,
      contracts: newContracts,
    }));
  };
  const onDelete = async (contract: any) => {
    const newContracts = siteData.contracts || [];
    await ApiCaller.delete(`contract/${contract.id}`);
    const idx = newContracts.findIndex((ct: any) => ct.id === contract.id);
    newContracts.splice(idx, 1);
    setSiteData((siteData: any) => ({
      ...siteData,
      contracts: newContracts,
    }));
  };
  return (
    <TabPane tabId="contract_tab">
      <Row>
        <Col xs={12}>
          <div style={styles.controls}>
            <Button
              label={t().action__create_contract}
              icon="add"
              type="primary"
              onClick={toggle}
            />
          </div>
        </Col>
      </Row>
      <ContractTable
        data={siteData.contracts?.filter((ct: any) => ct.action !== "delete")}
        contractTypes={contractTypes}
        onMarkAsRentalFee={async (contract) => {
          const res = await ApiCaller.post(
            `contract/${contract.id}/used_for_rental_fee_calculation`
          );
          if (res.status === "ok") {
            setSiteData({
              ...siteData,
              contracts: siteData.contracts.map((el: any) => {
                el.used_for_rental_fee_calculation = el.id === contract.id;
                return el;
              }),
            });
            alert("Success");
          }
        }}
        onDelete={onDelete}
        onEdit={(contract) => {
          setSelectedContract(contract);
          toggle();
        }}
        onDone={onDone}
      />
      <ModalCreateContract
        siteAddress={getContractLocation(siteData?.data)}
        onClosed={() => setSelectedContract(null)}
        isOpen={isOpenCreateContract}
        toggle={toggle}
        onApply={onApply}
        contractId={selectedContract?.id}
        contracts={siteData.contracts}
      />
    </TabPane>
  );
};

const styles = {
  controls: {
    paddingTop: 12,
  },
  contractContainer: {
    marginTop: 12,
  },
};

export default ContractTab;
